import React from "react";
import { Volume2, VolumeX, Plane, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { HeartIcon } from "./Deco";

export const TopBar = ({ lang, setLang, musicOn, toggleMusic, visible }) => (
  <div
    className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 sm:px-8 py-4 transition-all duration-700 ${
      visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6 pointer-events-none"
    }`}
  >
    <div className="flex items-center gap-2 gold-text font-serif tracking-[0.2em] text-sm">
      <Plane size={16} className="text-[var(--gold)] rotate-45" />
      <span className="hidden sm:inline">B &amp; F</span>
    </div>

    <div className="flex items-center gap-3">
      <button
        data-testid="music-toggle"
        onClick={toggleMusic}
        aria-label="music"
        className="w-10 h-10 rounded-full border border-[var(--gold)]/40 flex items-center justify-center text-[var(--gold)] backdrop-blur-sm bg-[var(--olive)]/30 hover:bg-[var(--olive)]/60 transition-colors"
      >
        {musicOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
      </button>

      <div className="flex items-center rounded-full border border-[var(--gold)]/40 overflow-hidden backdrop-blur-sm bg-[var(--olive)]/30 text-xs font-mono tracking-widest">
        {["tr", "en"].map((l) => (
          <button
            key={l}
            data-testid={`lang-${l}`}
            onClick={() => setLang(l)}
            className={`px-3 py-2 transition-colors ${
              lang === l ? "bg-gradient-to-r from-[#BF953F] to-[#B38728] text-[var(--olive)]" : "text-[var(--gold)]"
            }`}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  </div>
);

export const ScrollCue = ({ lenis, active, label }) => {
  const [show, setShow] = React.useState(true);
  React.useEffect(() => {
    const onScroll = () => setShow(window.scrollY < 260);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const go = () => {
    const target = window.scrollY + window.innerHeight * 0.9;
    if (lenis?.current) lenis.current.scrollTo(target, { duration: 1.4 });
    else window.scrollTo({ top: target, behavior: "smooth" });
  };
  return (
    <AnimatePresence>
      {active && show && (
        <motion.button
          data-testid="scroll-cue-btn"
          onClick={go}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-6 right-5 sm:right-7 z-40 flex flex-col items-center gap-2.5 group"
          aria-label="scroll down"
        >
          <span className="font-sans text-[10px] tracking-[0.32em] uppercase text-[var(--gold)]/80 group-hover:text-[var(--gold)] transition-colors">
            {label}
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.7, ease: "easeInOut" }}
            className="w-11 h-11 rounded-full bg-gradient-to-r from-[#BF953F] via-[#FCF6BA] to-[#B38728] flex items-center justify-center shadow-[0_6px_24px_rgba(191,149,63,0.5)] transition-transform group-hover:scale-110"
          >
            <ChevronDown size={20} className="text-[var(--olive)]" />
          </motion.span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export const Marquee = () => {
  const text = "MUHABBETLE BAŞLAYAN BİR ÖMÜR · A LIFETIME BEGUN WITH LOVE · TOGETHER FOREVER · ";
  return (
    <div className="leather border-y border-[var(--gold)]/20 py-5 overflow-hidden">
      <div className="marquee-track">
        {[0, 1].map((k) => (
          <span key={k} className="flex items-center">
            {Array.from({ length: 4 }).map((_, i) => (
              <span key={i} className="gold-text font-serif text-xl sm:text-3xl tracking-[0.15em] flex items-center">
                {text}
                <HeartIcon size={14} className="text-[var(--gold)] mx-4" />
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
};

export const Footer = ({ t, whatsapp }) => (
  <footer className="leather py-16 px-5 text-center">
    <p className="gold-text font-serif text-3xl sm:text-4xl">{t.closing}</p>
    <p className="font-script text-4xl sm:text-5xl text-[var(--gold)] mt-3">{t.script}</p>
    <div className="flex items-center justify-center gap-3 my-8 text-[var(--gold)]/60">
      <span className="h-px w-16 bg-current" />
      <HeartIcon size={12} className="text-[var(--gold)]" />
      <span className="h-px w-16 bg-current" />
    </div>
    <p className="font-mono text-[10px] tracking-[0.3em] text-[var(--gold)]/60">{t.contact}</p>
    <a href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer"
      data-testid="whatsapp-link"
      className="inline-block font-serif text-xl text-[var(--cream)] mt-2 hover:text-[var(--gold)] transition-colors">
      {whatsapp}
    </a>
    <p className="font-sans text-[11px] tracking-[0.2em] text-[var(--gold)]/40 mt-10">
      BÜŞRA &amp; FURKAN · 15.11.2026
    </p>
  </footer>
);
